import {Component, DoCheck, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Router, RouterLink} from '@angular/router';
import {DashboardResultComponent} from './dashboard-result/dashboard-result.component';
import {AlertModalService} from '../../common/component/modal';
import {PlanModelService} from '../../common/service/plan-model.service';
import {PlanLodgementService} from '../../common/service/plan-lodgement.service';
import {NlrPaginationComponent} from './dashboard-result/nlr-pagination.component';
import {PlanDetailComponent} from '../../plan-detail/plan-detail.component';
import {NgEventBus} from 'ng-event-bus';
import {PLAN_ACTION, DASHBOARD_TAB, PLAN_LODGEMENT_STAGE, LODGEMENT_TYPE} from '../../common/constants';
import {NlrpInnerComponentService} from './inner-component.service';
import {currentPlan, DashboardFilters} from './inner-component.model';
import {equals} from '../../common/service/data-util';
import {concatMap, map, Observable, of} from 'rxjs';
import {ModifyLodgedPlan} from 'src/app/common/models/modify-lodge-plan';

@Component({
    selector: 'nlr-inner',
    imports: [RouterLink, CommonModule, FormsModule, NlrPaginationComponent, DashboardResultComponent, PlanDetailComponent],
    templateUrl: './inner.component.html',
    styleUrl: './inner.component.scss'
})
export class InnerComponent implements OnInit, DoCheck {
    @ViewChild(DashboardResultComponent) dashboardResultComponent!: DashboardResultComponent;
    selectedTabType = DASHBOARD_TAB.IN_DESIGN;
    protected readonly PLAN_ACTION = PLAN_ACTION;
    protected readonly DASHBOARD_TAB = DASHBOARD_TAB;

    showEditPlanButton = true;
    showAmendPlanButton = true;
    showViewPlanButton = true;
    showViewPlanPdfButton = true;
    showAssignPlanButton = true;
    showReplacePlanButton = true;
    showRemovePlanButton = true;
    showAdditionalsButton = true;

    filters: DashboardFilters = new DashboardFilters();

    tabDescription = '';
    viewOptions = [
        {
            value: true,
            displayValue: 'My plans',
            toolTip: 'Plans created by or assigned to you'
        },
        {
            value: false,
            displayValue: 'Organisation plans',
            toolTip: 'Plans created by or assigned to you and others associated with your organisation'
        }
    ];

    tabDescriptions = {
        IN_DESIGN: 'In design',
        READY_FOR_QA: 'Ready for QA',
        READY_FOR_CERTIFICATION: 'Ready for certification',
        CERTIFIED: 'Ready for lodgement',
        LODGED: 'Lodged'
    };

    tabViewed = {
        IN_DESIGN: false,
        READY_FOR_QA: false,
        READY_FOR_CERTIFICATION: false,
        CERTIFIED: false,
        LODGED: false
    };

    planTypeSelected = true;

    customerPlans = [];
    customerOrganisationPlans = [];

    currentPlansData = [];
    filteredPlansData = [];

    tabCounters = {
        IN_DESIGN: 0,
        READY_FOR_QA: 0,
        READY_FOR_CERTIFICATION: 0,
        CERTIFIED: 0,
        LODGED: 0
    };

    currentPage = 1;
    pageSize = 20;
    sortBy = 'statusDate';
    sortDirection = '';
    dashboardResultSize = 0;

    startIndex = 0;
    endIndex = 0;
    filteredSize = 0;

    maxPageCount = 7;

    tabRouterLinkPath: string = '/plan-dashboard';

    constructor(
        private nlrpInnerComponentService: NlrpInnerComponentService,
        private alertModalService: AlertModalService,
        private planModelService: PlanModelService,
        private planLodgementService: PlanLodgementService,
        private eventBus: NgEventBus,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.selectTab(this.DASHBOARD_TAB.IN_DESIGN);
        this.populateCustomerPlans(false);
    }

    filtersAtLastCheck: DashboardFilters;

    ngDoCheck() {
        if (!equals(this.filters, this.filtersAtLastCheck)) {
            this.filtersAtLastCheck = structuredClone(this.filters);
            this.filterPlans();
            this.paginatedCurrentPlansData(1);
        }
    }

    paginatedCurrentPlansData(page: number) {
        this.startIndex = (page - 1) * this.pageSize;
        this.endIndex = this.startIndex + this.pageSize;
        this.filteredSize = this.filteredPlansData.slice(this.startIndex, this.endIndex).length;
        this.pageChange(page);
        this.nlrpInnerComponentService.updatePaginatedCurrentPlansData(this.filteredPlansData.slice(this.startIndex, this.endIndex));
    }

    pageChange(page: number) {
        this.currentPage = page;
    }

    assignPlanEvent() {
        this.populateCustomerPlans(false);
    }

    deletePlanEvent() {
        this.populateCustomerPlans(false);
    }

    modifyLodgedPlanEvent(modifyLodgedPlan: ModifyLodgedPlan) {
        const subs = this.populateCustomerPlans(false)
            .pipe(
                concatMap(() => {
                    const activeCustomerPlan = this.customerPlans.filter(plan => {
                        return (
                            plan.planLodgementStage !== PLAN_LODGEMENT_STAGE.LODGED && plan.planNumber === modifyLodgedPlan.planSelected.planNumber
                        );
                    });

                    const activeCustomerOrganisationPlan = this.customerOrganisationPlans.filter(function (plan) {
                        return (
                            plan.planLodgementStage !== PLAN_LODGEMENT_STAGE.LODGED && plan.planNumber === modifyLodgedPlan.planSelected.planNumber
                        );
                    });

                    let existingSummary = undefined;

                    if (activeCustomerPlan !== undefined && activeCustomerPlan.length > 0) {
                        existingSummary = activeCustomerPlan[0];
                    } else if (activeCustomerOrganisationPlan !== undefined && activeCustomerOrganisationPlan.length > 0) {
                        existingSummary = activeCustomerOrganisationPlan[0];
                    }

                    if (existingSummary !== undefined) {
                        const errorMessage =
                            modifyLodgedPlan.planSelected.planType.toUpperCase() +
                            ' ' +
                            modifyLodgedPlan.planSelected.planNumber +
                            ' has ongoing ' +
                            existingSummary.planLodgementType +
                            ' assigned to ' +
                            modifyLodgedPlan.planSelected.assignedToName;
                        this.alertModalService.createAlertMessage(errorMessage);
                        return of({});
                    } else {
                        if (
                            modifyLodgedPlan.lodgementType === LODGEMENT_TYPE.REPLACEMENT ||
                            modifyLodgedPlan.lodgementType === LODGEMENT_TYPE.AMENDMENT
                        ) {
                            return this.planLodgementService
                                .createNewPlanVersion(modifyLodgedPlan.planSelected.planLodgementId, modifyLodgedPlan.lodgementType)
                                .pipe(
                                    concatMap(plan => {
                                        this.showSuccessfulAmendOrReplaceMessage(modifyLodgedPlan.lodgementType);
                                        return of({edit: true, planLodgementId: plan.id});
                                    })
                                );
                        } else if (modifyLodgedPlan.useLatest) {
                            return this.selectLatestLodgedVersion(modifyLodgedPlan.planSelected).pipe(
                                map(response => {
                                    return {edit: false, planLodgementId: response.latestPlan.planLodgementId};
                                })
                            );
                        } else {
                            return of({edit: false, planLodgementId: modifyLodgedPlan.planSelected.planLodgementId});
                        }
                    }
                })
            )
            .subscribe(response => {
                if (response !== undefined) {
                    const planLodgementId = response['planLodgementId'];
                    response['edit']
                        ? this.router.navigate(['/plan/', ...Object.values({action: 'edit', planLodgementId})])
                        : this.router.navigate(['/plan/', ...Object.values({action: modifyLodgedPlan.action, planLodgementId})]);
                }
            });
        this.eventBus.cast('nlr:busy', subs);
    }

    requestLatestLodgedVersion(planSelected: currentPlan) {
        this.dashboardResultComponent.amendReplacePlanHandleLatestVersion(this.selectLatestLodgedVersion(planSelected));
    }

    selectLatestLodgedVersion(planSelected: currentPlan): Observable<any> {
        const lodgedCustomerPlan = this.customerPlans.filter(plan => {
            return (
                plan.planLodgementStage === PLAN_LODGEMENT_STAGE.LODGED &&
                plan.planNumber === planSelected.planNumber &&
                plan.stage === planSelected.stage &&
                plan.planPurpose === planSelected.planPurpose
            );
        });

        const lodgedCustomerOrganisationPlan = this.customerOrganisationPlans.filter(plan => {
            return (
                plan.planLodgementStage === PLAN_LODGEMENT_STAGE.LODGED &&
                plan.planNumber === planSelected.planNumber &&
                plan.stage === planSelected.stage &&
                plan.planPurpose === planSelected.planPurpose
            );
        });

        let lodgedPlans = [];

        if (lodgedCustomerPlan !== undefined && lodgedCustomerPlan.length > 0) {
            lodgedPlans.push.apply(lodgedPlans, lodgedCustomerPlan);
        }

        if (lodgedCustomerOrganisationPlan !== undefined && lodgedCustomerOrganisationPlan.length > 0) {
            lodgedPlans.push.apply(lodgedPlans, lodgedCustomerOrganisationPlan);
        }

        if (lodgedPlans !== undefined && lodgedPlans.length > 0) {
            return of({
                planSelected: planSelected,
                latestPlan: lodgedPlans
                    .sort(function (a, b) {
                        return a.version - b.version;
                    })
                    .pop()
            });
        }

        return of({});
    }

    populateCustomerPlans(resetFilters: boolean) {
        if (resetFilters) {
            this.resetFilters();
        }

        const sub1 = this.nlrpInnerComponentService.getMyPlans().subscribe({
            next: data => {
                if (data !== undefined) {
                    this.customerPlans = [...data];
                    this.planTypeChanged();
                    console.debug('Success retrieving customer plans');
                } else {
                    console.error('No customer plans available');
                    this.customerPlans = [];
                }
            },
            error: error => {
                const errorMessage = 'Error retrieving Customer Plans. ';
                console.error(errorMessage + error);
            }
        });
        this.eventBus.cast('nlr:busy', sub1);

        const sub2 = this.nlrpInnerComponentService.getOrganisationPlans().subscribe({
            next: data => {
                if (data !== undefined) {
                    this.customerOrganisationPlans = [...data];
                    this.planTypeChanged();

                    console.debug('Success retrieving customer plans');
                } else {
                    console.error('Error retrieving customer organisation plans.');
                }
            },
            error: error => {
                const errorMessage = 'Error retrieving Organisation Plans. ';
                console.error(errorMessage + error);
            }
        });
        this.eventBus.cast('nlr:busy', sub2);

        return of({});
    }

    resetFilters() {
        this.filters = new DashboardFilters();
        this.pageChange(1);
    }

    showAlert(tabPlanType: string): boolean {
        return this.getTabCounter(tabPlanType) > 0 && !this.tabViewed[tabPlanType];
    }

    adjustFontSize(increment: number) {
        const minFontSize = 12;
        const baseFontSize = 14;
        const maxFontSize = 20;
        const bodyElt = document.body;
        const fontSize = parseInt(bodyElt.style.fontSize) || baseFontSize;
        const newFontSize = fontSize + increment;

        if (newFontSize >= minFontSize && newFontSize <= maxFontSize) {
            bodyElt.style.fontSize = newFontSize + 'px';
        }
    }

    planTypeChanged() {
        if (this.planTypeSelected) {
            this.currentPlansData = [...this.customerPlans];
        } else {
            this.currentPlansData = [...this.customerOrganisationPlans];
        }

        this.initialSort();

        this.nlrpInnerComponentService.updateCurrentPlansData(this.currentPlansData);
        this.filterPlans();
        this.paginatedCurrentPlansData(1);

        this.tabCounters = {
            IN_DESIGN: 0,
            READY_FOR_QA: 0,
            READY_FOR_CERTIFICATION: 0,
            CERTIFIED: 0,
            LODGED: 0
        };

        this.currentPlansData.forEach(plan => {
            this.tabCounters[plan.planLodgementStage]++;
        });
    }

    getTabCounter(tabPlanType: string) {
        return this.tabCounters[tabPlanType];
    }

    isTabSelected(tabPlanType: string) {
        return this.selectedTabType === tabPlanType;
    }

    selectTab(tabPlanType: string) {
        this.tabDescription = this.tabDescriptions[tabPlanType];
        this.selectedTabType = tabPlanType;

        this.tabViewed[tabPlanType] = true;

        if (
            tabPlanType === this.DASHBOARD_TAB.IN_DESIGN ||
            tabPlanType === this.DASHBOARD_TAB.READY_FOR_QA ||
            tabPlanType === this.DASHBOARD_TAB.READY_FOR_CERTIFICATION ||
            tabPlanType === this.DASHBOARD_TAB.CERTIFIED
        ) {
            this.showEditPlanButton = true;
            this.showAmendPlanButton = false;
            this.showViewPlanButton = true;
            this.showViewPlanPdfButton = true;
            this.showAssignPlanButton = true;
            this.showReplacePlanButton = false;
            this.showRemovePlanButton = true;
            this.showAdditionalsButton = false;
        }

        if (tabPlanType === this.DASHBOARD_TAB.LODGED) {
            this.showEditPlanButton = false;
            this.showAmendPlanButton = true;
            this.showViewPlanButton = true;
            this.showViewPlanPdfButton = true;
            this.showAssignPlanButton = false;
            this.showReplacePlanButton = true;
            this.showRemovePlanButton = false;
            this.showAdditionalsButton = true;
        }

        this.pageChange(1);
        this.filterPlans();
        this.paginatedCurrentPlansData(1);
    }

    private filterMatch(value: string, filter: string) {
        return !filter || value?.toLowerCase().includes(filter.toLowerCase());
    }

    filterPlans() {
        this.filteredPlansData = this.currentPlansData.filter(data => {
            return (
                data.planLodgementStage === this.selectedTabType &&
                this.filterMatch(data.surveyorFirmName, this.filters.filterBySurveyorFirmNameValue) &&
                this.filterMatch(data.planType, this.filters.filterByPlanTypeValue) &&
                this.filterMatch(data.customerReference, this.filters.filterByCustomerReferenceValue) &&
                this.filterMatch(data.tenureType, this.filters.filterByTenureTypeValue) &&
                this.filterMatch(`${data.planNumber}`, this.filters.filterByPlanNumberValue) &&
                this.filterMatch(data.planPurpose, this.filters.filterByPurposeValue) &&
                this.filterMatch(data.description, this.filters.filterByDescriptionValue) &&
                this.filterMatch(data.creatorName, this.filters.filterByPlanCreatorValue) &&
                this.filterMatch(data.planLodgementType, this.filters.filterByPlanLodgementTypeValue) &&
                this.filterMatch(data.certifyingSurveyor, this.filters.filterByCertifyingSurveyorValue)
            );
        });
    }

    initialSort() {
        this.sortDirection = 'desc';
        this.currentPlansData.sort((a, b) => {
            return b.statusDate - a.statusDate;
        });
    }

    sortEvent($event: string) {
        const columnName = $event;
        if (this.sortBy === columnName) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortDirection = 'asc';
        }

        this.currentPlansData.sort((a, b) => {
            if (a[columnName] < b[columnName]) {
                return this.sortDirection === 'asc' ? -1 : 1;
            } else if (a[columnName] > b[columnName]) {
                return this.sortDirection === 'asc' ? 1 : -1;
            } else {
                return 0;
            }
        });
        this.filterPlans();
        this.paginatedCurrentPlansData(this.currentPage);
        this.sortBy = columnName;
    }

    parseDate(date: string): Date {
        const [day, month, year] = date.split('/').map(Number);
        return new Date(year, month - 1, day);
    }

    showSuccessfulAmendOrReplaceMessage(lodgementType: string) {
        var planType = lodgementType === LODGEMENT_TYPE.AMENDMENT ? 'The amendment plan ' : 'The replacement plan ';
        let model = this.planModelService.getModel();

        this.alertModalService.createAlertMessage(
            planType +
                model.planType.smpId +
                model.planNumber +
                ' version ' +
                model.versionNumber +
                " has been created and added to the 'In design' page of the dashboard.",
            'OK',
            'Information',
            'info'
        );
    }
}
